import { useEffect, useState } from 'react'
import { History, Loader2, PackagePlus } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import {
  useCancelPurchaseOrder,
  usePurchaseOrder,
  usePurchaseOrderRevision,
  useReceivePurchaseOrder,
  useSendPurchaseOrder,
} from '@/hooks/usePurchaseOrders'
import { AddLotModal } from '../AddLotModal'
import { PurchaseOrderEditorDialog } from './PurchaseOrderEditorDialog'
import { PurchaseOrderView } from './PurchaseOrderView'
import { PoPrintActions } from './document/PoPrintActions'
import { PoDocumentPreview } from './document/PoDocumentPreview'
import { PoStatusBadge, formatPoDate } from './poStatus'

interface PurchaseOrderDetailDialogProps {
  /** The order to show; null closes the dialog. */
  poId: string | null
  onClose: () => void
}

/**
 * One purchase order: the read view, its revision history, and the actions
 * its status allows (z8nrz7f7dz).
 *
 * - Draft: edit, send, cancel.
 * - Sent: edit (with a reason — that becomes the next revision), mark
 *   received, cancel.
 * - Received / Cancelled: read-only.
 *
 * Marking an order received records that it arrived; it does not book stock.
 * The lots still go in through Add lot, which needs what only the person at
 * the dock knows (vendor lot, expiry, where it went), so the dialog offers
 * Add lot for each line's material once the order is received.
 */
export function PurchaseOrderDetailDialog({ poId, onClose }: PurchaseOrderDetailDialogProps) {
  const { data: po, isLoading, error } = usePurchaseOrder(poId)
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.purchaseOrdersManage)

  const send = useSendPurchaseOrder()
  const receive = useReceivePurchaseOrder()
  const cancel = useCancelPurchaseOrder()

  const [editing, setEditing] = useState(false)
  const [pane, setPane] = useState<'preview' | 'details'>('preview')
  const [cancelling, setCancelling] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [viewRevision, setViewRevision] = useState<number | null>(null)
  const [receiveFor, setReceiveFor] = useState<{ materialId: string; label: string } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    setEditing(false)
    setCancelling(false)
    setCancelReason('')
    setViewRevision(null)
    setActionError(null)
  }, [poId])

  const snapshot = usePurchaseOrderRevision(
    poId,
    viewRevision !== null && viewRevision !== po?.revision ? viewRevision : null,
  )
  const shown = viewRevision !== null && viewRevision !== po?.revision ? snapshot.data : po

  const onError = (e: unknown) => setActionError(e instanceof Error ? e.message : 'That did not go through.')
  const busy = send.isPending || receive.isPending || cancel.isPending

  function doCancel() {
    if (!po) return
    if (!cancelReason.trim()) return setActionError('Give a reason for cancelling.')
    setActionError(null)
    cancel.mutate(
      { id: po.id, reason: cancelReason.trim() },
      { onSuccess: () => setCancelling(false), onError },
    )
  }

  const isOpen = po?.status === 'DRAFT' || po?.status === 'SENT'

  return (
    <>
      <Dialog open={poId !== null && !editing && receiveFor === null} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className='max-h-[92vh] overflow-y-auto sm:max-w-5xl'>
          <DialogHeader className='space-y-1'>
            <DialogTitle className='flex flex-wrap items-center gap-2'>
              {po?.number ?? 'Purchase order'}
              {po && <PoStatusBadge status={po.status} />}
              {po && <span className='text-sm font-normal text-muted-foreground'>Rev {po.revision}</span>}
            </DialogTitle>
            {po && (
              <p className='text-sm text-muted-foreground'>
                Created {formatPoDate(po.createdAt)}
                {po.createdBy ? ` by ${po.createdBy}` : ''}
                {po.sentAt ? ` · sent ${formatPoDate(po.sentAt)}` : ''}
                {po.receivedAt ? ` · received ${formatPoDate(po.receivedAt)}` : ''}
                {po.cancelledAt ? ` · cancelled ${formatPoDate(po.cancelledAt)}` : ''}
              </p>
            )}
          </DialogHeader>

          {isLoading ? (
            <div className='flex items-center gap-2 py-8 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading purchase order…
            </div>
          ) : error || !po ? (
            <p className='text-sm text-destructive'>
              {error instanceof Error ? error.message : 'Could not load the purchase order.'}
            </p>
          ) : (
            <div className='space-y-4'>
              {canManage && isOpen && viewRevision === null && (
                <div className='flex flex-wrap items-center gap-2'>
                  <Button size='sm' variant='outline' onClick={() => setEditing(true)} disabled={busy}>
                    Edit
                  </Button>
                  {po.status === 'DRAFT' && (
                    <Button
                      size='sm'
                      disabled={busy}
                      onClick={() => {
                        setActionError(null)
                        send.mutate(po.id, { onError })
                      }}
                    >
                      {send.isPending ? 'Sending…' : 'Mark as sent'}
                    </Button>
                  )}
                  {po.status === 'SENT' && (
                    <Button
                      size='sm'
                      disabled={busy}
                      onClick={() => {
                        setActionError(null)
                        receive.mutate(po.id, { onError })
                      }}
                    >
                      {receive.isPending ? 'Saving…' : 'Mark received'}
                    </Button>
                  )}
                  <Button
                    size='sm'
                    variant='outline'
                    className='border-red-200 text-red-600 hover:bg-red-50'
                    disabled={busy}
                    onClick={() => setCancelling((c) => !c)}
                  >
                    Cancel order
                  </Button>
                </div>
              )}

              {cancelling && isOpen && (
                <div className='space-y-2 rounded-lg border border-red-200 bg-red-50/40 p-3'>
                  <Textarea
                    rows={2}
                    aria-label='Reason for cancelling'
                    placeholder='Why is this order being cancelled?'
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                  <div className='flex gap-2'>
                    <Button size='sm' variant='destructive' onClick={doCancel} disabled={busy}>
                      {cancel.isPending ? 'Cancelling…' : 'Confirm cancellation'}
                    </Button>
                    <Button size='sm' variant='outline' onClick={() => setCancelling(false)}>
                      Keep order
                    </Button>
                  </div>
                </div>
              )}

              {actionError && <p className='text-sm text-destructive'>{actionError}</p>}

              {po.status === 'RECEIVED' && canManage && viewRevision === null && (
                <div className='space-y-2 rounded-lg border border-green-200 bg-green-50/40 p-3'>
                  <p className='text-sm text-gray-700'>
                    Received. Book the stock in with Add lot for each material:
                  </p>
                  <div className='flex flex-wrap gap-2'>
                    {po.lines.map((l) => (
                      <Button
                        key={l.id}
                        size='sm'
                        variant='outline'
                        className='gap-1'
                        onClick={() =>
                          setReceiveFor({
                            materialId: l.materialId,
                            label: l.description ?? l.articleNo ?? `Line ${l.lineNo}`,
                          })
                        }
                      >
                        <PackagePlus className='h-4 w-4' />
                        {l.description ?? l.articleNo ?? `Line ${l.lineNo}`} ({l.qty.toLocaleString('en-US')})
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {viewRevision !== null && viewRevision !== po.revision && (
                <div className='flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900'>
                  <span>Showing revision {viewRevision} as it stood then.</span>
                  <button type='button' className='font-medium underline' onClick={() => setViewRevision(null)}>
                    Back to current
                  </button>
                </div>
              )}

              {shown ? (
                <>
                  <PoPrintActions
                    key={shown.revision}
                    po={shown}
                    revision={viewRevision !== null && viewRevision !== po.revision ? viewRevision : null}
                  />
                  {/* What the supplier will receive, without printing; or the fields. */}
                  <div className='flex gap-1 rounded-lg bg-muted p-1 w-fit' role='group' aria-label='Purchase order view'>
                    {(
                      [
                        ['preview', 'Preview'],
                        ['details', 'Details'],
                      ] as const
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        type='button'
                        aria-pressed={pane === value}
                        onClick={() => setPane(value)}
                        className={cn(
                          'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                          pane === value ? 'bg-white text-gray-900 shadow-sm' : 'text-muted-foreground hover:text-gray-900',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {pane === 'preview' ? <PoDocumentPreview po={shown} /> : <PurchaseOrderView po={shown} />}
                </>
              ) : (
                <div className='flex items-center gap-2 py-6 text-sm text-muted-foreground'>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Loading revision…
                </div>
              )}

              {po.revisions.length > 0 && (
                <div className='space-y-1'>
                  <p className='flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500'>
                    <History className='h-3.5 w-3.5' />
                    Revision history
                  </p>
                  <ul className='divide-y rounded-lg border text-sm'>
                    {[...po.revisions]
                      .sort((a, b) => b.revision - a.revision)
                      .map((r) => {
                        const current = r.revision === po.revision
                        const active = (viewRevision ?? po.revision) === r.revision
                        return (
                          <li key={r.revision}>
                            <button
                              type='button'
                              className={cn(
                                'flex w-full flex-wrap items-center gap-x-3 px-3 py-2 text-left hover:bg-muted/40',
                                active && 'bg-muted/60',
                              )}
                              onClick={() => setViewRevision(current ? null : r.revision)}
                            >
                              <span className='font-medium text-gray-900'>Rev {r.revision}</span>
                              {current && <span className='text-xs text-muted-foreground'>current</span>}
                              <span className='text-gray-600'>{r.reason ?? 'Original'}</span>
                              <span className='ml-auto text-xs text-muted-foreground'>
                                {formatPoDate(r.createdAt)}
                                {r.createdBy ? ` · ${r.createdBy}` : ''}
                              </span>
                            </button>
                          </li>
                        )
                      })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PurchaseOrderEditorDialog po={editing && po ? po : null} onClose={() => setEditing(false)} />

      <AddLotModal
        materialId={receiveFor?.materialId ?? null}
        materialLabel={receiveFor?.label}
        open={receiveFor !== null}
        onClose={() => setReceiveFor(null)}
      />
    </>
  )
}
